import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiTarget, FiTrendingUp, FiPieChart, FiAlertCircle, 
  FiCheckCircle, FiRefreshCw, FiArrowRight, FiInfo, 
  FiDollarSign, FiUsers, FiLayers, FiZap, FiSmartphone 
} = FiIcons;

const BUSINESS_TYPES = {
  'dog-walking': { label: 'Dog Walking', tip: 'Typical: $25–$35/walk. High frequency (3-5x week) leads to high LTGP.' },
  'grooming': { label: 'Grooming', tip: 'Typical: $70–$120/session. 6-8 week recurring cycles are standard.' },
  'training': { label: 'Training', tip: 'Typical: $150–$300/session or $1k+ packages. Higher Max CAC is possible here.' },
  'boarding': { label: 'Boarding', tip: 'Typical: $50–$80/night. High seasonality—focus ads 4-6 weeks before holidays.' },
  'daycare': { label: 'Daycare', tip: 'Typical: $35–$50/day. Subscription models significantly increase client lifespan.' },
  'pet-sitting': { label: 'Pet Sitting', tip: 'Typical: $75–$125/day. Trust is the #1 driver of conversion.' },
  'pet-store': { label: 'Pet Store', tip: 'Typical: $40–$60 AOV. Focus on high-margin services/specialty items for ads.' }
};

const PHASES = [
  { id: 'phase1', label: 'Phase 1: Track', desc: 'I need to find my numbers.', icon: FiTarget },
  { id: 'phase2', label: 'Phase 2: Test', desc: 'I have numbers, I need to test ads.', icon: FiTrendingUp },
  { id: 'phase3', label: 'Phase 3: Scale', desc: 'My ads work, I want to grow.', icon: FiLayers }
];

export default function AdBudgetCalculator() {
  const [formData, setFormData] = useState({
    businessType: '',
    avgMonthlyRevenue: '',
    avgMonthlyCost: '',
    lifespanMonths: '',
    newClientsGoal: '',
    phase: 'phase1'
  });

  const [showResults, setShowResults] = useState(false);

  const results = useMemo(() => {
    if (!showResults) return null;
    
    const monthlyRev = parseFloat(formData.avgMonthlyRevenue) || 0;
    const monthlyCost = parseFloat(formData.avgMonthlyCost) || 0;
    const lifespan = parseFloat(formData.lifespanMonths) || 0;
    const newClientsGoal = parseFloat(formData.newClientsGoal) || 0;
    const phase = formData.phase;

    const monthlyProfit = monthlyRev - monthlyCost;
    
    // LTGP = (Monthly Revenue - Monthly Cost) * Client Lifespan in months
    const ltgp = monthlyProfit * lifespan;
    
    // Max CAC (1/3 of LTGP)
    const maxCac = ltgp / 3;
    
    // Phase 2 Test Budget: 2x gross profit in first 30 days
    const testBudget = 2 * monthlyProfit;
    
    // Phase 3 Scale Budget: (Wanted * MaxCac) * 1.2
    const scaleBudget = (newClientsGoal * maxCac) * 1.2;

    let recommendedMonthly = 0;
    if (phase === 'phase1') {
      recommendedMonthly = 0;
    } else if (phase === 'phase2') {
      recommendedMonthly = testBudget;
    } else {
      recommendedMonthly = scaleBudget;
    }

    const dailyBudget = recommendedMonthly / 30;

    return {
      ltgp,
      maxCac,
      recommendedMonthly,
      dailyBudget,
      testBudget,
      monthlyProfit
    };
  }, [formData, showResults]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getVerdict = () => {
    const cac = Math.round(results.maxCac);
    const ltgpValue = results.ltgp;
    
    if (ltgpValue < 300) return {
      color: 'text-red-500',
      bg: 'bg-red-50',
      title: 'Not Ready',
      text: "Your numbers are not ready for ads yet. Either your prices are too low or your costs are too high. Fix the math first, then run ads."
    };
    
    if (ltgpValue >= 300 && ltgpValue <= 600) return {
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      title: 'Borderline Model',
      text: `Your margin is tight but workable. Keep your cost per new client well under $${cac}. Start small, track everything, and do not scale until you see proof it is working.`
    };

    return {
      color: 'text-green-600',
      bg: 'bg-green-50',
      title: 'Scalable Model',
      text: `Good margin. Ads can work here. Your max cost per new client is $${cac} and that is your ceiling, not your starting point. Start with your first-month gross profit doubled and test from there.`
    };
  };

  return (
    <>
      <SEO 
        title="Ad Budget Calculator for Pet Businesses | Otso Co" 
        description="Calculate your ideal marketing spend using the $100M Leads framework. Designed for dog walkers, groomers, and pet care pros." 
        path="/ad-budget-calculator" 
      />
      
      <div className="min-h-screen bg-brand-white text-brand-darkText">
        <section className="bg-brand-primaryDark text-white py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-black mb-4">Ad Budget Calculator</h1>
            <p className="text-brand-blush text-lg font-medium opacity-90">
              Find your ideal marketing spend based on the $100M Leads framework.
            </p>
          </div>
        </section>

        <section className="py-12 px-4 -mt-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              
              <div className="lg:col-span-7 space-y-8">
                <div className="bg-white p-6 md:p-10 rounded-[32px] shadow-xl border border-gray-100">
                  <h2 className="text-2xl font-bold text-brand-primaryDark mb-8 flex items-center gap-2">
                    <SafeIcon icon={FiTarget} className="text-brand-mediumPurple" />
                    Business Metrics
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">Business Type</label>
                      <select 
                        name="businessType" 
                        value={formData.businessType} 
                        onChange={handleInputChange}
                        className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-mediumPurple outline-none transition-all bg-gray-50"
                      >
                        <option value="">Select your service...</option>
                        {Object.entries(BUSINESS_TYPES).map(([id, data]) => (
                          <option key={id} value={id}>{data.label}</option>
                        ))}
                      </select>
                      {formData.businessType && (
                        <p className="text-xs text-brand-mediumPurple font-medium bg-brand-mediumPurple/5 p-3 rounded-lg flex gap-2">
                          <SafeIcon icon={FiInfo} className="shrink-0 mt-0.5" />
                          {BUSINESS_TYPES[formData.businessType].tip}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1">
                        Avg Monthly Revenue / Client <SafeIcon icon={FiDollarSign} size={14} />
                      </label>
                      <input 
                        type="number" 
                        name="avgMonthlyRevenue"
                        value={formData.avgMonthlyRevenue}
                        onChange={handleInputChange}
                        placeholder="e.g. 300"
                        className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-mediumPurple outline-none transition-all"
                      />
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest pl-1">
                        Total monthly revenue from one client on average.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">Avg Monthly Cost / Client</label>
                      <input 
                        type="number" 
                        name="avgMonthlyCost"
                        value={formData.avgMonthlyCost}
                        onChange={handleInputChange}
                        placeholder="e.g. 50"
                        className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-mediumPurple outline-none transition-all"
                      />
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest pl-1">
                        Supplies, time, gas — whatever it costs you to serve one client per month.
                      </p>
                    </div>

                    <div className="md:col-span-2 space-y-2">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">Client Lifespan (Months)</label>
                      <input 
                        type="number" 
                        name="lifespanMonths"
                        value={formData.lifespanMonths}
                        onChange={handleInputChange}
                        placeholder="e.g. 12"
                        className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-mediumPurple outline-none transition-all"
                      />
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest pl-1">
                        How many months does a typical client stay with you?
                      </p>
                    </div>
                  </div>

                  <hr className="my-10 border-gray-100" />

                  <h2 className="text-2xl font-bold text-brand-primaryDark mb-6 flex items-center gap-2">
                    <SafeIcon icon={FiLayers} className="text-brand-mediumPurple" />
                    Growth Phase
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    {PHASES.map(phase => (
                      <button
                        key={phase.id}
                        onClick={() => setFormData(prev => ({ ...prev, phase: phase.id }))}
                        className={`p-4 rounded-2xl border-2 text-left transition-all ${
                          formData.phase === phase.id 
                          ? 'border-brand-mediumPurple bg-brand-mediumPurple/5 shadow-inner' 
                          : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${
                          formData.phase === phase.id ? 'bg-brand-mediumPurple text-white' : 'bg-gray-100 text-gray-400'
                        }`}>
                          <SafeIcon icon={phase.icon} />
                        </div>
                        <p className="font-bold text-brand-primaryDark text-sm">{phase.label}</p>
                        <p className="text-[10px] text-gray-500 mt-1">{phase.desc}</p>
                      </button>
                    ))}
                  </div>

                  {formData.phase === 'phase3' && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      className="mb-8 space-y-2"
                    >
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider">How many NEW clients do you want per month?</label>
                      <input 
                        type="number" 
                        name="newClientsGoal"
                        value={formData.newClientsGoal}
                        onChange={handleInputChange}
                        placeholder="e.g. 10"
                        className="w-full p-4 rounded-xl border-2 border-brand-mediumPurple/30 focus:border-brand-mediumPurple outline-none transition-all"
                      />
                    </motion.div>
                  )}

                  <button 
                    onClick={() => { setShowResults(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="w-full mt-6 bg-brand-primaryDark hover:bg-[#1f2552] text-white py-5 rounded-2xl font-black text-xl transition-all shadow-xl flex items-center justify-center gap-3"
                  >
                    Calculate My Budget <SafeIcon icon={FiArrowRight} />
                  </button>
                </div>
              </div>

              <div className="lg:col-span-5">
                <AnimatePresence mode="wait">
                  {!showResults ? (
                    <motion.div 
                      key="placeholder"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-brand-primaryDark/5 h-full min-h-[400px] rounded-[40px] border-4 border-dashed border-brand-primaryDark/10 flex flex-col items-center justify-center p-10 text-center"
                    >
                      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg mb-6 text-brand-primaryDark opacity-50">
                        <SafeIcon icon={FiPieChart} size={40} />
                      </div>
                      <h3 className="text-2xl font-bold text-brand-primaryDark/40">Enter your numbers to see your recommendation.</h3>
                    </motion.div>
                  ) : (
                    <motion.div 
                      key="results"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="space-y-6 sticky top-24"
                    >
                      <div className="bg-brand-primaryDark text-white p-8 md:p-10 rounded-[40px] shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 opacity-10">
                          <SafeIcon icon={FiTrendingUp} size={120} />
                        </div>
                        <p className="text-brand-blush font-black uppercase tracking-widest text-xs mb-2">Recommended Monthly Budget</p>
                        <h3 className="text-5xl md:text-6xl font-black mb-4">${Math.round(results.recommendedMonthly).toLocaleString()}</h3>
                        
                        {formData.phase === 'phase1' ? (
                          <div className="bg-white/10 p-5 rounded-2xl border border-white/10 mb-8 backdrop-blur-sm">
                            <p className="text-sm font-medium leading-relaxed text-brand-blush">
                              Don't run ads yet. You need to know your actual numbers first. Fill in your revenue, costs, and how long clients stick around, then come back.
                            </p>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-white/70 font-bold mb-8">
                            <SafeIcon icon={FiCheckCircle} className="text-brand-blush" />
                            Target Daily Spend: ${Math.round(results.dailyBudget)}/day
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4 pt-8 border-t border-white/10">
                          <div>
                            <p className="text-[10px] text-white/50 uppercase font-black tracking-widest mb-1">LTGP</p>
                            <p className="text-xl font-bold">${Math.round(results.ltgp).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-white/50 uppercase font-black tracking-widest mb-1">Max CAC</p>
                            <p className="text-xl font-bold text-brand-blush">${Math.round(results.maxCac)}</p>
                          </div>
                        </div>
                      </div>

                      <div className={`${getVerdict().bg} p-6 rounded-3xl border border-gray-100`}>
                        <div className="flex items-center gap-3 mb-3">
                          <SafeIcon icon={FiAlertCircle} className={getVerdict().color} size={24} />
                          <h4 className={`font-black uppercase tracking-wider text-sm ${getVerdict().color}`}>
                            Verdict: {getVerdict().title}
                          </h4>
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {getVerdict().text}
                        </p>
                      </div>

                      <div className="bg-white p-8 rounded-[32px] shadow-lg border border-gray-100">
                        <h4 className="text-lg font-bold text-brand-primaryDark mb-6 flex items-center gap-2">
                          <SafeIcon icon={FiPieChart} className="text-brand-mediumPurple" />
                          Budget Allocation
                        </h4>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                            <div className="flex items-center gap-3">
                              <SafeIcon icon={FiSmartphone} className="text-brand-mediumPurple" />
                              <span className="text-sm font-bold text-gray-700">Digital Ads (FB/IG)</span>
                            </div>
                            <span className="font-mono font-bold">${Math.round(results.recommendedMonthly * 0.7).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                            <div className="flex items-center gap-3">
                              <SafeIcon icon={FiRefreshCw} className="text-brand-mediumPurple" />
                              <span className="text-sm font-bold text-gray-700">Retargeting</span>
                            </div>
                            <span className="font-mono font-bold">${Math.round(results.recommendedMonthly * 0.1).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center p-4 bg-brand-blush/10 rounded-xl border border-brand-blush/20">
                            <div className="flex items-center gap-3">
                              <SafeIcon icon={FiZap} className="text-brand-primaryDark" />
                              <span className="text-sm font-bold text-brand-primaryDark">Content/Awareness Ads</span>
                            </div>
                            <span className="font-mono font-bold">${Math.round(results.recommendedMonthly * 0.2).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-brand-mediumPurple p-8 rounded-[32px] text-white shadow-xl">
                        <h4 className="text-xl font-bold mb-4">What's Next for {PHASES.find(p => p.id === formData.phase).label}?</h4>
                        <p className="text-white/90 text-sm leading-relaxed mb-6">
                          {formData.phase === 'phase1' && "Your goal is to focus on your operations. Don't scale until you know exactly where your clients are coming from. Use a simple CRM or spreadsheet."}
                          {formData.phase === 'phase2' && `Run your test for 30 days. Your goal is to acquire at least one client for less than $${Math.round(results.maxCac)}. If you do, you're ready for Phase 3.`}
                          {formData.phase === 'phase3' && `You have $${Math.round(results.maxCac)} to play with. Max out your digital channels first, then layer on retargeting to dominate your service area.`}
                        </p>
                        <a 
                          href="https://tidycal.com/digital-by-perry/schedule-a-call" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-brand-primaryDark text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-[#1f2552] transition-all"
                        >
                          Build My Custom Strategy <SafeIcon icon={FiArrowRight} />
                        </a>
                      </div>

                      <button 
                        onClick={() => setShowResults(false)}
                        className="w-full text-gray-400 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:text-brand-primaryDark transition-colors py-4"
                      >
                        <SafeIcon icon={FiRefreshCw} /> Reset Calculator
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16 px-4 border-t border-gray-100">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-brand-primaryDark mb-4">Want a custom strategy built around these numbers?</h2>
            <p className="text-gray-600 mb-8">Otso Co works exclusively with pet care businesses to turn these numbers into real bookings.</p>
            <a 
              href="https://tidycal.com/digital-by-perry/schedule-a-call" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-brand-mediumPurple hover:bg-brand-mauve text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all shadow-lg inline-flex items-center gap-3"
            >
              Book a Strategy Call <SafeIcon icon={FiArrowRight} />
            </a>
          </div>
        </section>
      </div>
    </>
  );
}